// src/shared/components/FormInput.jsx
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A reusable input component with optional icon, helper text, and validation.
 */
export default function FormInput({
  id,
  name,
  type,
  label,
  value,
  onChange,
  placeholder,
  error,
  icon,          // pass icon class like "bi bi-envelope-fill"
  helperText,
  required,
  disabled,
  autoComplete,
  rows,           // if rows is provided, render textarea instead of input
  className,
  style,
  inputClassName,
  ...rest
}) {
  const hasError = !!error;
  // Support both "bi-icon-name" and "bi bi-icon-name" for Bootstrap Icons
  const iconClass = icon && !icon.includes(' ') ? `bi ${icon}` : icon;
  const inputClasses = classNames(
    'form-control',
    { 'is-invalid': hasError },
    inputClassName
  );

  const InputElement = rows ? 'textarea' : 'input';

  const inputProps = {
    id,
    name,
    className: inputClasses,
    placeholder,
    value,
    onChange,
    required,
    disabled,
    autoComplete,
    style,
    ...rest
  };

  if (!rows) {
    inputProps.type = type;
  } else {
    inputProps.rows = rows;
  }

  return (
    <div className={classNames('mb-3', className)}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && (
            <span
              className="text-danger ms-1"
              aria-hidden="true"
              title="Required"
            >
              *
            </span>
          )}
        </label>
      )}

      {icon ? (
        <div className="position-relative">
          <InputElement
            {...inputProps}
            className={classNames(inputClasses, 'ps-5')}
          />
          <i
            className={classNames(iconClass, 'position-absolute top-50 start-0 translate-middle-y ms-3 text-muted')}
            style={{ fontSize: '1.1rem' }}
          />
        </div>
      ) : (
        <InputElement {...inputProps} />
      )}

      {helperText && !hasError && (
        <div className="form-text">{helperText}</div>
      )}

      {hasError && (
        <div className="invalid-feedback">{error}</div>
      )}
    </div>
  );
}

FormInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  icon: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  rows: PropTypes.number,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  style: PropTypes.object,
};

FormInput.defaultProps = {
  type: 'text',
  name: undefined,
  label: null,
  value: '',
  placeholder: '',
  error: null,
  icon: null,
  helperText: null,
  required: false,
  disabled: false,
  autoComplete: undefined,
  rows: undefined,
  className: '',
  inputClassName: '',
  style: {},
};
